import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { LiquidacionService } from 'src/app/core/services/liquidacion.service';
import { NativeBridgeService } from 'src/app/core/services/native-bridge.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { UserService } from 'src/app/core/services/user.services';
import { IOptionsNgSelect } from 'src/app/shared/interfaces/ioptionsNgSelect';

@Component({
  selector: 'app-descargar-liquidacion-popup',
  templateUrl: './descargar-liquidacion-popup.component.html',
  styleUrls: ['./descargar-liquidacion-popup.component.scss']
})
export class DescargarLiquidacionPopupComponent implements OnInit { 
  @ViewChild('branchMatSelect') selectBranch: MatSelect;
  form: FormGroup;
  isLoading = false;
  buttonLabel = 'Descargar';
  maxDate: Date = new Date();
  loadingFinish: boolean = false;
  branchOfficesCollection: IOptionsNgSelect[];
  branchOfficeSelect: number[];
  branchFilter: IOptionsNgSelect[];
  sucursalActual = null;
  minDate: Date;
  maxDateTo: Date;

  get branchSelected() {
    return this.form.get('branchSelected');
  }
  constructor(
    private fb: FormBuilder,
    private liquidacionService: LiquidacionService,
    private dialogRef: MatDialogRef<DescargarLiquidacionPopupComponent>,
    private nativeBridgeService: NativeBridgeService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private userService: UserService
  ) {
    this.minDate = this.calculateMinDate();
  }

  ngOnInit(): void {
    this.loadbranchOffices();
    this.createForm();
  }

  calculateMinDate(): Date {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  }

  createForm() {
    const today = new Date();
    const thirtyOneDaysAgo = new Date();
    thirtyOneDaysAgo.setDate(today.getDate() - 30);

    this.form = this.fb.group(
      {
        dateDesde: [thirtyOneDaysAgo, [Validators.required]],
        dateHasta: [today, [Validators.required]],
        searchBranch: new UntypedFormControl(''),
        branchSelected: new UntypedFormControl('', Validators.required)
      },
      { validator: this.dateRangeValidator('dateDesde', 'dateHasta') }
    );

    // Establecer maxDateTo inicial
    this.setMaxDate(thirtyOneDaysAgo);

    // Cuando cambia dateDesde, limpiar dateHasta y actualizar maxDateTo
    this.form.get('dateDesde')?.valueChanges.subscribe((dateFromValue) => {
      if (dateFromValue) {
        this.clearDateTo();
        this.setMaxDate(dateFromValue);
      }
    });
  }

  dateRangeValidator(dateFromControlName: string, dateToControlName: string): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      const dateFromControl = formGroup.get(dateFromControlName);
      const dateToControl = formGroup.get(dateToControlName);

      if (!dateFromControl || !dateToControl) {
        // Los controles de fecha no están presentes, no se aplica la validación
        return null;
      }

      let dateFrom = dateFromControl.value;
      let dateTo = dateToControl.value;

      // Convertir a Date si es string
      if (dateFrom && !(dateFrom instanceof Date)) {
        dateFrom = new Date(dateFrom);
      }
      if (dateTo && !(dateTo instanceof Date)) {
        dateTo = new Date(dateTo);
      }

      if (!dateFrom || !dateTo || isNaN(dateFrom.getTime()) || isNaN(dateTo.getTime())) {
        return null;
      }

      // Validación de días (máximo 31 días de rango = 30 días de diferencia)
      const millisecondsInDay = 24 * 60 * 60 * 1000;
      const differenceInDays = Math.abs((dateTo.getTime() - dateFrom.getTime()) / millisecondsInDay);
      if (differenceInDays > 30) {
        return { dateRangeError: true };
      }

      // Validación de horas
      if (dateFrom > dateTo) {
        return { dateRangeInvalid: true };
      }

      return null; // Ambas validaciones pasan
    };
  }

  public translate(key: string): string {
    return this.translateService.instant(key);
  }
  selectionChangeBranch() {
    this.sucursalActual = this.selectBranch.value;
    this.form.get('branchSelected').setValue(this.sucursalActual);
  }

  setMaxDate(dateFrom: Date): void {
    if (dateFrom) {
      const maxDateFrom = new Date(dateFrom);
      maxDateFrom.setDate(maxDateFrom.getDate() + 30);
      this.maxDateTo = maxDateFrom;
    }
  }

  clearDateTo(): void {
    const dateToControl = this.form.get('dateHasta');
    dateToControl?.setValue(null);
    dateToControl?.markAsTouched();
    dateToControl?.updateValueAndValidity();
  }

  private loadbranchOffices() {
    this.userService.getBranchOfficesCollection().then((officeBranches) => {
      this.branchOfficesCollection = Array<IOptionsNgSelect>();
      officeBranches.forEach((ob) => {
        const opt = { value: ob.id, display: ob.name, type: ob.name };
        this.branchOfficesCollection.push(opt as IOptionsNgSelect);
      });
      this.branchFilter = this.branchOfficesCollection;
    });
  }

  generarLiquidacion() {
    const fechaDesde = this.form.get('dateDesde').value;
    const fechaHasta = this.form.get('dateHasta').value;
    const sucursales = [this.form.get('branchSelected').value.toString()];
    this.loadingFinish = true;
    this.liquidacionService
      .downloadLiquidacionFile(fechaDesde, fechaHasta, sucursales)
      .then((message) => {
        this.loadingFinish = false;
        this.notificationService.showSuccess(message);
        this.dialogRef.close();
      })
      .catch((error) => {
        this.notificationService.showError(error?.message);
        this.dialogRef.close();
      });
  }
}
